import { lazy } from "react";

/**
 * lazyRetry — React.lazy with chunk-load recovery (Oct 2026, P2 brief).
 *
 * Clarity logged "Loading chunk 309 failed (timeout)" in slow in-app
 * browsers, leaving shoppers on a blank screen. Recovery ladder:
 *   1. Retry the dynamic import once after a short pause (covers a
 *      transient timeout on a flaky mobile connection).
 *   2. If it fails again, reload the page ONCE — after re-fetching the
 *      current URL with `cache: "reload"` so a stale cached index.html
 *      (pointing at chunk hashes from a previous deploy) is replaced.
 *   3. A sessionStorage guard stops a reload loop; the third failure
 *      surfaces to the nearest error boundary like before.
 */
const GUARD_PREFIX = "tbb_chunk_reload_";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function hasReloaded(name) {
  try { return sessionStorage.getItem(GUARD_PREFIX + name) === "1"; } catch { return false; }
}

function markReloaded(name) {
  try { sessionStorage.setItem(GUARD_PREFIX + name, "1"); } catch { /* private mode */ }
}

async function cacheBustReload() {
  try {
    await fetch(window.location.href, { cache: "reload", credentials: "same-origin" });
  } catch { /* offline — reload anyway */ }
  window.location.reload();
}

export function lazyRetry(importer, name = "chunk") {
  return lazy(async () => {
    try {
      const mod = await importer();
      try { sessionStorage.removeItem(GUARD_PREFIX + name); } catch { /* ignore */ }
      return mod;
    } catch (firstErr) {
      await sleep(1200);
      try {
        return await importer();
      } catch (err) {
        if (typeof window !== "undefined" && !hasReloaded(name)) {
          markReloaded(name);
          try { console.warn("[lazyRetry] chunk failed twice, reloading", name, err && err.message); } catch { /* ignore */ }
          await cacheBustReload();
          // Keep the promise pending while the page unloads.
          return new Promise(() => {});
        }
        throw firstErr;
      }
    }
  });
}
